import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { Button, Fields } from '@ets-global/b2c-website-ui';
import { useMutation } from '@apollo/client';

import { hasValidLength } from '../../../helpers/Validators/PasswordValidator';

import { CHANGE_USER_PASSWORD } from '../../../graphql/mutations';
import AccountBlock from '../../Account/Block';
import { useNotification } from '../../../hooks';
import errorsHandler from '../../../helpers/errorsHandler';
import FormikObserver from '../../FormikObserver';
import useYup from '../../../hooks/useYup';
import useDataLayer from '../../../hooks/useDataLayer';

const ChangePassword = () => {
  const { t } = useTranslation();
  const { Yup } = useYup();
  const [changePassword] = useMutation(CHANGE_USER_PASSWORD);
  const { newNotification } = useNotification();
  const { pushEventFormError } = useDataLayer();
  const [isFormUpdated, setFormUpdated] = useState(false);

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required(),
    newPassword: Yup.string()
      .test('hasValidLength', t('common.form.password.min-length'), (value) => hasValidLength(value))
      .notOneOf([Yup.ref('currentPassword'), null], t('password.form.similar-password'))
      .required(),
  });

  const onSubmit = async (data, actions) => {
    try {
      const {
        data: { authResult },
      } = await changePassword({
        variables: {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        },
      });

      if (authResult.__typename === 'Error') {
        const errorMessage = t(`password.form.${authResult.reason}`);
        actions.setErrors({ newPassword: errorMessage });
        pushEventFormError(errorMessage);
      } else {
        newNotification({
          content: t('user-account.change-password.message.created'),
          type: 'success',
        });
      }

      setFormUpdated(false);
    } catch (error) {
      errorsHandler({ error, actions, newNotification, t });
    }

    actions.setSubmitting(false);
  };

  return (
    <AccountBlock title={t('user-account.change-password.title')} className={'account-infos'}>
      <Formik
        initialValues={{ currentPassword: '', newPassword: '' }}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        render={({ isSubmitting, values }) => {
          return (
            <Form className="change-password__form">
              <Fields.FormikFastField
                id={'currentPassword'}
                name={'currentPassword'}
                component={Fields.PasswordField}
                label={t('user-account.change-password.form.label.current-password')}
                autoComplete={'current-password'}
                required={true}
              />
              <Fields.FormikFastField
                id={'newPassword'}
                name={'newPassword'}
                component={Fields.PasswordField}
                label={t('user-account.change-password.form.label.new-password')}
                autoComplete={'new-password'}
                required={true}
                withStrength={true}
              />
              <div className="account-infos__password-tips">
                <Trans>common.form.password.length-tips</Trans>
              </div>
              <Button
                disabled={!isFormUpdated}
                loading={isSubmitting}
                className="form__button form__submit"
                type="submit"
                aria-label={`${t('user-account.change-password.form.label.save')} button`}
              >
                <Trans>user-account.change-password.form.label.save</Trans>
              </Button>
              <FormikObserver values={values} onChange={() => setFormUpdated(true)} />
            </Form>
          );
        }}
      />
    </AccountBlock>
  );
};

export default ChangePassword;
